/**
 * RespImage - Core Component (Standalone)
 *
 * This is a standalone responsive image component using native HTML picture/source
 * elements. It does NOT wrap a shadcn/ui component or use Next.js Image.
 *
 * Features:
 * - Always renders picture element with source elements for optimal browser selection
 * - Generates srcset from candidates array for responsive sizing
 * - Art direction support: Different images for different breakpoints
 * - Priority loading: Sets loading="eager", fetchPriority="high" and emits a
 *   <link rel="preload"> via ReactDOM.preload (no art direction variants only)
 * - Object-fit options: cover, contain, fill, none, scale-down
 * - Automatic fallback to smallest image for browsers without picture support
 *
 * Structure:
 * <picture>
 *   <source media="..." srcSet="..." />  // Art direction (optional)
 *   <source srcSet="..." sizes="..." />  // Primary srcset
 *   <img src="..." alt="..." />          // Fallback
 * </picture>
 *
 * Props:
 * - srcsetCandidates: Array of { imageUrl, width, height }
 * - artDirection: Array of { media, srcsetCandidates } for breakpoint-specific images
 * - sizes: Responsive sizes attribute (default: '100vw')
 * - priority: Enable eager loading for above-the-fold images
 *
 * @see resp-image.types.ts for type definitions
 */
'use client'

import * as React from 'react';
import { preload } from 'react-dom';
import { emptyToUndefined } from '@/app/_lib/utils';
import type { RespImageProps, SrcsetCandidate } from './resp-image.types';

/**
 * Generates srcset string from candidates
 * @param candidates - Array of srcset candidates
 * @returns Formatted srcset string
 */
function generateSrcset(candidates: SrcsetCandidate[]): string {
    return candidates
        .map((candidate) => `${candidate.imageUrl} ${candidate.width}w`)
        .join(', ');
}

/**
 * Gets the smallest image candidate (used as fallback src)
 * @param candidates - Array of srcset candidates
 * @returns Smallest candidate by width
 */
function getSmallestCandidate(candidates: SrcsetCandidate[]): SrcsetCandidate {
    return candidates.reduce(
        (smallest, current) => (current.width < smallest.width ? current : smallest),
        candidates[0]
    );
}

/**
 * RespImage Component
 *
 * A responsive image component that always renders a picture element
 * with source elements for optimal browser image selection.
 *
 * Features:
 * - Always uses picture element with source elements
 * - Primary srcset rendered as source (not in img directly)
 * - Supports art direction via multiple source elements
 * - Generates optimized srcset from candidates
 * - Full TypeScript support
 * - Accessibility built-in (alt, title)
 *
 * Rendered structure:
 * <picture>
 *   <source media="..." srcSet="..." /> // Art direction (optional)
 *   <source srcSet="..." sizes="..." />  // Primary srcset
 *   <img src="..." alt="..." />          // Fallback only
 * </picture>
 *
 * @example
 * // Simple responsive image (WebP) - Full width hero
 * <RespImage
 *   srcsetCandidates={[
 *     { imageUrl: '/img/hero-400.webp', width: 400, height: 300 },
 *     { imageUrl: '/img/hero-800.webp', width: 800, height: 600 },
 *     { imageUrl: '/img/hero-1200.webp', width: 1200, height: 900 },
 *   ]}
 *   alt="Hero image"
 *   sizes="100vw"
 * />
 *
 * @example
 * // With art direction (different crops for mobile/desktop)
 * <RespImage
 *   srcsetCandidates={mobileCandidates}
 *   artDirection={[
 *     {
 *       media: '(min-width: 768px)',
 *       srcsetCandidates: desktopCandidates
 *     }
 *   ]}
 *   alt="Responsive banner"
 *   priority
 * />
 */
export default function RespImage({
    srcsetCandidates,
    artDirection,
    alt,
    title,
    sizes = '100vw',
    className,
    pictureClassName,
    loading = 'lazy',
    priority = false,
    objectFit = 'cover',
    onLoad,
    onError,
    editorAttributes,
}: RespImageProps) {
    // Validate srcsetCandidates
    if (srcsetCandidates.length === 0) {
        console.warn('RespImage: No srcsetCandidates provided');
        return null;
    }

    // Get smallest candidate for fallback src (old browsers = likely slow connection)
    const fallbackCandidate = getSmallestCandidate(srcsetCandidates);

    // Generate srcset string for primary image
    const primarySrcset = generateSrcset(srcsetCandidates);

    // Override loading if priority is set
    const effectiveLoading = priority ? 'eager' : loading;

    // Preload the LCP candidate. React's automatic image preloading skips
    // <img> inside <picture>, so priority images would otherwise only be
    // discovered when the parser reaches them deep in the HTML. Skipped for
    // art direction: a preload carries no media condition, so it would fetch
    // the wrong variant on non-matching viewports (double download).
    if (priority && (!artDirection || artDirection.length === 0)) {
        preload(fallbackCandidate.imageUrl, {
            as: 'image',
            imageSrcSet: primarySrcset,
            imageSizes: sizes,
            fetchPriority: 'high',
        });
    }

    // Build img class with object-fit
    const objectFitMap = {
        contain: 'object-contain',
        cover: 'object-cover',
        fill: 'object-fill',
        none: 'object-none',
        'scale-down': 'object-scale-down',
    };
    const objectFitClass = objectFitMap[objectFit];
    const imgClassName = [className, objectFitClass].filter(Boolean).join(' ');

    // Render picture element with sources
    return (
        <picture className={pictureClassName}>
            {/* Art direction sources (if provided) */}
            {artDirection?.map((variant, index) => (
                <source
                    key={`art-${variant.media}-${index}`}
                    media={variant.media}
                    srcSet={generateSrcset(variant.srcsetCandidates)}
                    sizes={emptyToUndefined(variant.sizes) ?? sizes}
                    type={variant.type}
                />
            ))}

            {/* Primary srcset as source element */}
            <source
                srcSet={primarySrcset}
                sizes={sizes}
            />

            {/* Fallback img element (no srcset, just src for browsers without picture support) */}
            <img
                src={fallbackCandidate.imageUrl}
                alt={alt}
                title={emptyToUndefined(title)}
                width={fallbackCandidate.width}
                height={fallbackCandidate.height}
                loading={effectiveLoading}
                fetchPriority={priority ? 'high' : undefined}
                className={imgClassName}
                onLoad={onLoad}
                onError={onError}
                {...editorAttributes}
            />
        </picture>
    );
}

export { RespImage };
